import React, { useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { classNames } from '../../utils/classNames';

type PasswordInputProps = {
    label?: string;
    register?: UseFormRegisterReturn;
    placeholder?: string;
    error?: string;
    containerStyle?: string;
    inputValue?: string | number;
    showValue?: boolean;
    inputUniqueName?: string;
    inputStyle?: string;
    hideInputSpace?: boolean;
    index?: any;
    disabled?: boolean;
    dataQa?: string;
    parent?: any;
    labelStyle?: string;
    helperText?: string;
    onHandleKeyDown?: (e: any, type?: string, index?: any) => void;
    onChangeInput?: (
        value: string | null | undefined,
        type: string,
        index?: any,
        parent?: any
    ) => void;
};

const PasswordInput: React.FC<PasswordInputProps> = ({
    register,
    placeholder = 'Password',
    error,
    label,
    containerStyle,
    inputValue,
    showValue,
    labelStyle,
    inputUniqueName,
    inputStyle,
    hideInputSpace,
    index,
    disabled,
    dataQa,
    parent,
    helperText,
    onChangeInput,
    onHandleKeyDown,
}) => {
    const [show, setShow] = useState(false);

    return (
        <div className={containerStyle || 'mb-3'}>
            {!!label && (
                <label className={classNames('block text-primary-light text-[18px] mb-2', labelStyle || '')}>
                    {label}
                </label>
            )}

            <div className='relative'>
                <input
                    {...register}
                    disabled={disabled || false}
                    type={show ? 'text' : 'password'}
                    {...(showValue && {
                        value: (inputValue as string) || '',
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                            onChangeInput?.(e.target.value, inputUniqueName || '', index, parent),
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                            onHandleKeyDown?.(e, inputUniqueName || '', index),
                    })}
                    data-qa={dataQa || ''}
                    placeholder={placeholder}
                    autoComplete='current-password'
                    className={classNames(
                        'border border-borderColor rounded-[16px] w-full px-4 py-3 pr-12 bg-white',
                        'text-[#1C1C1E] placeholder:text-[#8A8A8E] text-base',
                        error ? 'border-red-500' : '',
                        inputStyle || ''
                    )}
                />

                {/* show/hide toggle */}
                <button
                    type='button'
                    tabIndex={-1}
                    onClick={() => setShow((s) => !s)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-gray-50'
                    aria-label={show ? 'Hide password' : 'Show password'}
                    title={show ? 'Hide password' : 'Show password'}
                >
                    {show ? (
                        // Eye (visible)
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.8'
                            className='h-5 w-5 text-gray-700'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 5 12 5c4.64 0 8.577 2.51 9.964 6.678.07.21.07.434 0 .644C20.577 16.49 16.64 19 12 19c-4.64 0-8.577-2.51-9.964-6.678z'
                            />
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                            />
                        </svg>
                    ) : (
                        // Eye-off (hidden)
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.8'
                            className='h-5 w-5 text-gray-700'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.25 19 12 19c1.79 0 3.48-.42 4.98-1.177M6.228 6.228A10.45 10.45 0 0112 5c4.75 0 8.774 2.662 10.066 6.999a10.523 10.523 0 01-2.29 3.58M3 3l18 18'
                            />
                        </svg>
                    )}
                </button>
            </div>

            {error ? (
                <span className='mt-2 block text-xs text-red-500'>{error}</span>
            ) : (
                <>
                    {helperText ? (
                        <p className='mt-2 text-sm text-gray-400'>{helperText}</p>
                    ) : !hideInputSpace ? (
                        <span className='block h-2 mt-2' />
                    ) : null}
                </>
            )}
        </div>
    );
};

export default PasswordInput;
